import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  serviceCategoryLabels,
  serviceCategoryOptions,
  type ServiceCategory,
} from "../rfqs/api.js";
import { useCreateContractorProfile } from "../contractorProfiles/api.js";
import { ApiError } from "../api/client.js";

type FieldName =
  | "businessName"
  | "contactName"
  | "phone"
  | "email"
  | "website"
  | "serviceCategories"
  | "serviceArea"
  | "yearsExperience"
  | "licenseInfo"
  | "insuranceInfo"
  | "about"
  | "notes";

type Errors = Partial<Record<FieldName, string>>;

const MAX_LENGTHS: Partial<Record<FieldName, number>> = {
  businessName: 120,
  contactName: 80,
  phone: 30,
  email: 120,
  website: 300,
  serviceArea: 200,
  licenseInfo: 300,
  insuranceInfo: 300,
  about: 1200,
  notes: 1200,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Permissive but catches obvious typos: "(312) 555-1212", "312-555-1212",
// "+1 312 555 1212", "3125551212". Requires at least 7 digits total.
const PHONE_RE = /^[+(]?[\d][\d\s().-]{6,}$/;

function digitCount(s: string): number {
  let n = 0;
  for (const ch of s) if (ch >= "0" && ch <= "9") n++;
  return n;
}

function validateField(name: FieldName, raw: string, categoryCount: number): string | undefined {
  const value = raw.trim();
  const max = MAX_LENGTHS[name];
  if (max && value.length > max) {
    return `Please keep this under ${max} characters.`;
  }
  switch (name) {
    case "businessName":
      if (!value) return "Business name is required.";
      if (value.length < 2) return "Business name is too short.";
      return undefined;
    case "contactName":
      if (!value) return "Your name is required.";
      if (value.length < 2) return "Please enter your full name.";
      return undefined;
    case "phone":
      if (!value) return "Phone number is required.";
      if (!PHONE_RE.test(value) || digitCount(value) < 7) {
        return "Enter a phone number with at least 7 digits, like (312) 555-1212.";
      }
      return undefined;
    case "email":
      if (!value) return "Email is required.";
      if (!EMAIL_RE.test(value)) {
        return "That doesn't look like a valid email — try name@example.com.";
      }
      return undefined;
    case "website":
      if (!value) return undefined;
      if (/\s/.test(value)) return "Website can't contain spaces.";
      // Allow bare domains (server prepends https://). Just require a dot.
      if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value) && !/\./.test(value)) {
        return "Enter a website like example.com or https://example.com.";
      }
      return undefined;
    case "serviceArea":
      if (!value) return "Tell us where you work (cities or counties).";
      if (value.length < 2) return "Service area is too short.";
      return undefined;
    case "yearsExperience": {
      if (!value) return "Years of experience is required.";
      const n = Number(value);
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        return "Enter a whole number of years.";
      }
      if (n < 0) return "Years can't be negative.";
      if (n > 80) return "Please enter 80 or fewer years.";
      return undefined;
    }
    case "serviceCategories":
      if (categoryCount === 0) return "Pick at least one service category.";
      return undefined;
    default:
      return undefined;
  }
}

/** Map a server Zod issue path back to one of our field names. */
function fieldFromIssuePath(path: unknown): FieldName | undefined {
  if (!Array.isArray(path) || path.length === 0) return undefined;
  const head = path[0];
  if (typeof head !== "string") return undefined;
  const known: ReadonlyArray<FieldName> = [
    "businessName",
    "contactName",
    "phone",
    "email",
    "website",
    "serviceCategories",
    "serviceArea",
    "yearsExperience",
    "licenseInfo",
    "insuranceInfo",
    "about",
    "notes",
  ];
  return (known as readonly string[]).includes(head) ? (head as FieldName) : undefined;
}

function friendlyServerMessage(field: FieldName, fallback: string): string {
  switch (field) {
    case "email":
      return "That doesn't look like a valid email — try name@example.com.";
    case "website":
      return "Enter a website like example.com or https://example.com.";
    case "phone":
      return "Enter a phone number with at least 7 digits, like (312) 555-1212.";
    case "serviceCategories":
      return "Pick at least one service category.";
    case "yearsExperience":
      return "Enter a whole number of years between 0 and 80.";
    default:
      return fallback || "Please correct this field.";
  }
}

function mapServerErrors(err: unknown): { field?: Errors; banner?: string } {
  if (!(err instanceof ApiError)) {
    return {
      banner: err instanceof Error ? err.message : "Something went wrong. Please try again.",
    };
  }
  const body = err.body as { error?: string; details?: unknown } | null;
  const details = body?.details;
  const field: Errors = {};
  if (Array.isArray(details)) {
    for (const issue of details) {
      if (!issue || typeof issue !== "object") continue;
      const path = (issue as { path?: unknown }).path;
      const message = String((issue as { message?: unknown }).message ?? "");
      const f = fieldFromIssuePath(path);
      if (f && !field[f]) field[f] = friendlyServerMessage(f, message);
    }
  }
  if (Object.keys(field).length === 0) {
    return {
      banner: body?.error ?? `Submission failed (HTTP ${err.status}).`,
    };
  }
  return {
    field,
    banner: "Please fix the highlighted fields and try again.",
  };
}

export function ContractorIntake() {
  const create = useCreateContractorProfile();
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [yearsExperience, setYearsExperience] = useState("0");
  const [licenseInfo, setLicenseInfo] = useState("");
  const [insuranceInfo, setInsuranceInfo] = useState("");
  const [about, setAbout] = useState("");
  const [notes, setNotes] = useState("");
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);

  const submitted = create.isSuccess;

  function clearError(name: FieldName) {
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function handleBlur(name: FieldName, value: string) {
    const msg = validateField(name, value, categories.length);
    setErrors((prev) => {
      if (!msg) {
        if (!(name in prev)) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: msg };
    });
  }

  function toggleCategory(category: ServiceCategory) {
    setCategories((prev) => {
      const next = prev.includes(category)
        ? prev.filter((v) => v !== category)
        : [...prev, category];
      // Live-clear the category error as soon as one is picked.
      if (next.length > 0) clearError("serviceCategories");
      return next;
    });
  }

  function validateAll(): Errors {
    const fields: FieldName[] = [
      "businessName",
      "contactName",
      "phone",
      "email",
      "website",
      "serviceCategories",
      "serviceArea",
      "yearsExperience",
      "licenseInfo",
      "insuranceInfo",
      "about",
      "notes",
    ];
    const values: Record<FieldName, string> = {
      businessName,
      contactName,
      phone,
      email,
      website,
      serviceCategories: "",
      serviceArea,
      yearsExperience,
      licenseInfo,
      insuranceInfo,
      about,
      notes,
    };
    const next: Errors = {};
    for (const f of fields) {
      const msg = validateField(f, values[f], categories.length);
      if (msg) next[f] = msg;
    }
    return next;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBanner(null);
    const next = validateAll();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      setBanner("Please fix the highlighted fields and try again.");
      const first = Object.keys(next)[0];
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
        el?.focus();
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
      return;
    }
    setErrors({});
    try {
      await create.mutateAsync({
        businessName: businessName.trim(),
        contactName: contactName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        website: website.trim() || undefined,
        serviceCategories: categories,
        serviceArea: serviceArea.trim(),
        yearsExperience: Number.parseInt(yearsExperience, 10),
        licenseInfo: licenseInfo.trim() || undefined,
        insuranceInfo: insuranceInfo.trim() || undefined,
        about: about.trim() || undefined,
        notes: notes.trim() || undefined,
      });
    } catch (err) {
      const mapped = mapServerErrors(err);
      if (mapped.field) setErrors((prev) => ({ ...prev, ...mapped.field }));
      setBanner(mapped.banner ?? "Submission failed. Please try again.");
    }
  }

  if (submitted) {
    return (
      <main className="mx-auto w-full max-w-2xl p-4 sm:p-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <h1 className="text-xl font-semibold text-emerald-900">Submission received</h1>
          <p className="mt-2 text-sm text-emerald-900">
            Thank you. Your contractor profile has been submitted.
          </p>
          <p className="mt-4 text-sm">
            <Link className="font-medium text-teal-700 underline" to="/contractor-submissions">
              View all submissions
            </Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl p-4 sm:p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Contractor intake form</h1>
      <p className="mt-2 text-sm text-slate-600">
        Fill this out once. We will review your submission and follow up.
      </p>

      {banner && (
        <div
          role="alert"
          className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
        >
          {banner}
        </div>
      )}

      <form className="mt-5 space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="Business name" htmlFor="businessName" error={errors.businessName}>
          <input
            id="businessName"
            data-field="businessName"
            value={businessName}
            onChange={(e) => {
              setBusinessName(e.target.value);
              if (errors.businessName) clearError("businessName");
            }}
            onBlur={(e) => handleBlur("businessName", e.target.value)}
            aria-invalid={!!errors.businessName}
            className={inputClass(!!errors.businessName)}
          />
        </Field>

        <Field label="Your name" htmlFor="contactName" error={errors.contactName}>
          <input
            id="contactName"
            data-field="contactName"
            autoComplete="name"
            value={contactName}
            onChange={(e) => {
              setContactName(e.target.value);
              if (errors.contactName) clearError("contactName");
            }}
            onBlur={(e) => handleBlur("contactName", e.target.value)}
            aria-invalid={!!errors.contactName}
            className={inputClass(!!errors.contactName)}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Phone" htmlFor="phone" error={errors.phone}>
            <input
              id="phone"
              data-field="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(312) 555-1212"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (errors.phone) clearError("phone");
              }}
              onBlur={(e) => handleBlur("phone", e.target.value)}
              aria-invalid={!!errors.phone}
              className={inputClass(!!errors.phone)}
            />
          </Field>
          <Field label="Email" htmlFor="email" error={errors.email}>
            <input
              id="email"
              data-field="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) clearError("email");
              }}
              onBlur={(e) => handleBlur("email", e.target.value)}
              aria-invalid={!!errors.email}
              className={inputClass(!!errors.email)}
            />
          </Field>
        </div>

        <Field
          label="Website (optional)"
          htmlFor="website"
          hint="No need to type https:// — we'll add it for you."
          error={errors.website}
        >
          <input
            id="website"
            data-field="website"
            type="text"
            inputMode="url"
            autoComplete="url"
            placeholder="example.com"
            value={website}
            onChange={(e) => {
              setWebsite(e.target.value);
              if (errors.website) clearError("website");
            }}
            onBlur={(e) => handleBlur("website", e.target.value)}
            aria-invalid={!!errors.website}
            className={inputClass(!!errors.website)}
          />
        </Field>

        <Field label="Service categories" error={errors.serviceCategories}>
          <div
            data-field="serviceCategories"
            tabIndex={-1}
            className={`grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-2 ${errors.serviceCategories ? "border-rose-400" : "border-slate-200"}`}
          >
            {serviceCategoryOptions.map((category) => (
              <label key={category} className="flex min-h-[40px] items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={categories.includes(category)}
                  onChange={() => toggleCategory(category)}
                />
                <span>{serviceCategoryLabels[category]}</span>
              </label>
            ))}
          </div>
        </Field>

        <Field label="Service area" htmlFor="serviceArea" error={errors.serviceArea}>
          <input
            id="serviceArea"
            data-field="serviceArea"
            placeholder="Cities or counties you serve"
            value={serviceArea}
            onChange={(e) => {
              setServiceArea(e.target.value);
              if (errors.serviceArea) clearError("serviceArea");
            }}
            onBlur={(e) => handleBlur("serviceArea", e.target.value)}
            aria-invalid={!!errors.serviceArea}
            className={inputClass(!!errors.serviceArea)}
          />
        </Field>

        <Field label="Years of experience" htmlFor="yearsExperience" error={errors.yearsExperience}>
          <input
            id="yearsExperience"
            data-field="yearsExperience"
            type="number"
            inputMode="numeric"
            min={0}
            max={80}
            value={yearsExperience}
            onChange={(e) => {
              setYearsExperience(e.target.value);
              if (errors.yearsExperience) clearError("yearsExperience");
            }}
            onBlur={(e) => handleBlur("yearsExperience", e.target.value)}
            aria-invalid={!!errors.yearsExperience}
            className={inputClass(!!errors.yearsExperience)}
          />
        </Field>

        <Field label="License info (optional)" htmlFor="licenseInfo" error={errors.licenseInfo}>
          <textarea
            id="licenseInfo"
            data-field="licenseInfo"
            rows={2}
            value={licenseInfo}
            onChange={(e) => {
              setLicenseInfo(e.target.value);
              if (errors.licenseInfo) clearError("licenseInfo");
            }}
            onBlur={(e) => handleBlur("licenseInfo", e.target.value)}
            aria-invalid={!!errors.licenseInfo}
            className={inputClass(!!errors.licenseInfo)}
          />
        </Field>

        <Field
          label="Insurance info (optional)"
          htmlFor="insuranceInfo"
          error={errors.insuranceInfo}
        >
          <textarea
            id="insuranceInfo"
            data-field="insuranceInfo"
            rows={2}
            value={insuranceInfo}
            onChange={(e) => {
              setInsuranceInfo(e.target.value);
              if (errors.insuranceInfo) clearError("insuranceInfo");
            }}
            onBlur={(e) => handleBlur("insuranceInfo", e.target.value)}
            aria-invalid={!!errors.insuranceInfo}
            className={inputClass(!!errors.insuranceInfo)}
          />
        </Field>

        <Field label="About your business (optional)" htmlFor="about" error={errors.about}>
          <textarea
            id="about"
            data-field="about"
            rows={4}
            value={about}
            onChange={(e) => {
              setAbout(e.target.value);
              if (errors.about) clearError("about");
            }}
            onBlur={(e) => handleBlur("about", e.target.value)}
            aria-invalid={!!errors.about}
            className={inputClass(!!errors.about)}
          />
        </Field>

        <Field label="Notes (optional)" htmlFor="notes" error={errors.notes}>
          <textarea
            id="notes"
            data-field="notes"
            rows={3}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              if (errors.notes) clearError("notes");
            }}
            onBlur={(e) => handleBlur("notes", e.target.value)}
            aria-invalid={!!errors.notes}
            className={inputClass(!!errors.notes)}
          />
        </Field>

        <button
          type="submit"
          disabled={create.isPending}
          className="min-h-[44px] w-full rounded-md bg-teal-700 px-4 text-sm font-medium text-white disabled:opacity-60"
        >
          {create.isPending ? "Submitting..." : "Submit"}
        </button>
      </form>
    </main>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="block">
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-sm text-rose-700">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function inputClass(invalid: boolean): string {
  const base =
    "block w-full min-h-[44px] rounded-md border px-3 text-base focus:outline-none focus:ring-1";
  return invalid
    ? `${base} border-rose-400 focus:border-rose-500 focus:ring-rose-400`
    : `${base} border-slate-300 focus:border-teal-700 focus:ring-teal-700`;
}
